import {
  Box,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import { details, formControl, signIn } from "../login/Login.style";
import { useEffect, useRef, useState } from "react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AppButton from "../../base/AppButton.component";
import LoginImage from "../../../assets/images/delivery.png";
import { useNavigate } from "react-router-dom";
import { InputValidation } from "../../../utils/InputValidationRegex";
import { useLazyQuery, useMutation } from "@apollo/client";
import { OMNI_LOGIN_SERVICE } from "../../../graphql/mutation/mutation";
import { FETCH_EMPLOYEE_DEALERSHIPS } from "../../../graphql/query/query";
import { useAuth } from "../../../context/Auth.context";
import { useAppContext } from "../../../context/App.context";
import moment from "moment";

const dropdownSx = {
  borderRadius: "8px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(0,171,85,0.6)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#00ab55",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#00ab55",
    borderWidth: "2px",
  },
  "& .MuiSelect-select": {
    fontWeight: 600,
    fontSize: "15px",
    color: "#00ab55",
    textAlign: "center",
  },
  "& .MuiSvgIcon-root": {
    color: "#00ab55",
  },
};

const subtextSx = {
  fontSize: "12px",
  color: "#637381",
  textAlign: "center",
  mt: 0.5,
  mb: 1,
};

const OmniLoginPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const auth = useAuth();
  const app = useAppContext();

  const [loginForm, setLoginForm] = useState({
    employeeCode: "",
    showPassword: true,
    employeeCodeError: "",
    loginDate: moment(new Date().toISOString()).format("yyyy-MM-DD"),
  });

  const [dealerships, setDealerships] = useState([]);
  const [selectedDealershipCode, setSelectedDealershipCode] = useState("");

  const debounceRef = useRef(null);

  // Step 1: as soon as a valid employee code is entered, look up the
  // dealerships associated with that employee.
  const [fetchDealerships, { loading: dealershipsLoading }] = useLazyQuery(
    FETCH_EMPLOYEE_DEALERSHIPS,
    {
      fetchPolicy: "network-only",
      onError: (err) => {
        console.log(err);
        setDealerships([]);
        setSelectedDealershipCode("");
      },
      onCompleted: (data) => {
        const result = data?.fetchEmployeeDealerships;

        if (result?.errors?.[0]?.message) {
          setLoginForm((prev) => ({
            ...prev,
            employeeCodeError: result.errors[0].message,
          }));
          setDealerships([]);
          setSelectedDealershipCode("");
          return;
        }

        const list = result?.dealerships ?? [];
        setDealerships(list);
        setSelectedDealershipCode(list.length === 1 ? list[0].dealership_code : "");
      },
    }
  );

  // Step 2: only once a dealership has been chosen and Login is pressed,
  // authenticate against that specific dealership.
  const [fetchLogin, { loading: loginLoading }] = useMutation(
    OMNI_LOGIN_SERVICE,
    {
      onError: (err) => {
        console.log(err);
      },
      onCompleted: (data) => {
        const result = data?.omniLoginService;

        if (result?.errors?.[0]?.message) {
          setLoginForm((prev) => ({
            ...prev,
            employeeCodeError: result.errors[0].message,
          }));
          return;
        }

        auth.login(result);
        app.setOmniLoginDate(loginForm.loginDate);
        navigate(
          `/express/getUserDetails/${result.user_id}/${result.dealership_id}/${result.verification_workflow_id}`
        );
      },
    }
  );

  useEffect(() => {
    const pinValid =
      loginForm.employeeCode !== "" &&
      !InputValidation.employeeCodeValidation(loginForm.employeeCode);

    if (pinValid) {
      setDealerships([]);
      setSelectedDealershipCode("");

      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchDealerships({
          variables: { employee_code: loginForm.employeeCode },
        });
      }, 300);
    } else {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setDealerships([]);
      setSelectedDealershipCode("");
    }

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginForm.employeeCode]);

  const handleFinalLogin = () => {
    if (!selectedDealershipCode || loginForm.employeeCode === "") return;

    fetchLogin({
      variables: {
        omniLoginInput: {
          employee_code: loginForm.employeeCode,
          dealership_code: selectedDealershipCode,
        },
      },
    });
  };

  const handleChange = (prop) => (event) => {
    const value = event.target.value;
    let errorText;

    switch (prop) {
      case "employeeCode":
        if (InputValidation.employeeCodeValidation(value))
          errorText = "Invalid Pin Number";
        if (value === "") errorText = "";
        setLoginForm((prev) => ({
          ...prev,
          employeeCode: value,
          employeeCodeError: errorText,
        }));
        break;
      default:
        setLoginForm((prev) => ({ ...prev, [prop]: value }));
        break;
    }
  };

  const handleClickShowPassword = () => {
    setLoginForm((prev) => ({
      ...prev,
      showPassword: !prev.showPassword,
    }));
  };

  const isMultiStore = dealerships.length > 1;
  const showDropdown = dealerships.length > 0;
  const loginEnabled = showDropdown && selectedDealershipCode !== "";

  return (
    <>
      <Box
        component="form"
        noValidate
        autoComplete="off"
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          width: { xs: "90%" },
        }}
      >
        <img
          alt="Login"
          src={LoginImage}
          width={180}
          style={{
            marginBottom: "40px",
            display: "flex",
            alignSelf: "center",
          }}
        />

        <Typography variant="h6" component="div" sx={signIn}>
          Sign in to DeliveryIQ+
        </Typography>

        <Typography variant="span" component="div" sx={details(theme)}>
          Enter your details below
        </Typography>

        <FormControl sx={formControl} variant="outlined">
          <InputLabel color={loginForm.employeeCodeError ? "error" : "primary"}>
            6 digit pin
          </InputLabel>
          <OutlinedInput
            label="6 digit pin"
            type={loginForm.showPassword ? "password" : "text"}
            value={loginForm.employeeCode}
            onChange={handleChange("employeeCode")}
            sx={{ borderRadius: 2 }}
            error={!!loginForm.employeeCodeError}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClickShowPassword}
                  onMouseDown={(event) => event.preventDefault()}
                  edge="end"
                >
                  {loginForm.showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
          <FormHelperText
            error={!!loginForm.employeeCodeError}
            sx={{ color: theme.palette.error.main }}
          >
            {loginForm.employeeCodeError || " "}
          </FormHelperText>
        </FormControl>

        {showDropdown && (
          <Box sx={{ display: "flex", flexDirection: "column", mb: 1 }}>
            <FormControl fullWidth size="medium" variant="outlined">
              <Select
                displayEmpty
                value={selectedDealershipCode}
                onChange={(e) => setSelectedDealershipCode(e.target.value)}
                sx={dropdownSx}
                IconComponent={KeyboardArrowDownIcon}
                renderValue={(value) => {
                  if (!value) return "Select Dealership";
                  const option = dealerships.find(
                    (d) => d.dealership_code === value
                  );
                  return option ? option.dealership_name : "Select Dealership";
                }}
                MenuProps={{
                  PaperProps: { style: { maxHeight: 320 } },
                }}
              >
                {dealerships.map((option, i) => (
                  <MenuItem
                    key={option.dealership_id ?? i}
                    value={option.dealership_code}
                  >
                    {option.dealership_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography sx={subtextSx}>
              You are signing into the above dealership
            </Typography>
          </Box>
        )}

        <AppButton
          buttonText={"Login"}
          sx={{ m: 1 }}
          loading={dealershipsLoading || loginLoading}
          disabled={!loginEnabled || dealershipsLoading || loginLoading}
          clickHandler={handleFinalLogin}
        />
      </Box>
    </>
  );
};
export default OmniLoginPage;